import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { IconChevronLeft, IconChevronDown } from "@tabler/icons-react";
import { cn, copyToClipboard } from "@/lib/utils";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { Button } from "@/lib/components/Button/Button";
import {
  ActionIdentifier,
  applyAnimatedImageCustomInput,
  applySourceImageCustomInput,
  type InspirationAnimationPrompt,
  type InspirationGuide as InspirationGuideType,
  type InspirationGuideStep,
  type InspirationImagePrompt,
} from "@/lib/stores/editor-store/creative-project-state";
import Toast from "@/lib/components/Toast";
import { AnalyticsApi } from "@/lib/api/analytics-api";
import type { MainViewState } from "../creative-project-hooks";
import { InspirationPromptListItem } from "./InspirationPromptListItem";
import { subscribeInspirationAction } from "./inspiration-action-events";

type TabId = "INSTRUCTIONS" | "ANIMATION_PROMPTS" | "IMAGE_PROMPTS";

const TAB_LABELS: Record<TabId, string> = {
  INSTRUCTIONS: "Instructions",
  ANIMATION_PROMPTS: "Animation prompts",
  IMAGE_PROMPTS: "Image prompts",
};

const tabContentVariants = {
  enter: (direction: number) => ({ x: `${direction * 100}%` }),
  center: { x: "0%" },
  exit: (direction: number) => ({ x: `${direction * -100}%` }),
};

function getStepPromptTab(step: InspirationGuideStep): TabId | null {
  switch (step.actionIdentifier) {
    case ActionIdentifier.GENERATE_IMAGE:
      return "IMAGE_PROMPTS";
    case ActionIdentifier.ANIMATE_VIDEO:
      return "ANIMATION_PROMPTS";
    default:
      return null;
  }
}

function InspirationGuide({
  guide,
  mainView,
  onBack,
}: {
  guide: InspirationGuideType;
  mainView: MainViewState;
  onBack: () => void;
}) {
  const availableTabs = useMemo<TabId[]>(() => {
    const tabs: TabId[] = [];
    if (guide.steps.length > 0) tabs.push("INSTRUCTIONS");
    if (guide.animationPrompts && guide.animationPrompts.length > 0)
      tabs.push("ANIMATION_PROMPTS");
    if (guide.imagePrompts && guide.imagePrompts.length > 0)
      tabs.push("IMAGE_PROMPTS");
    return tabs;
  }, [guide]);

  const [activeTab, setActiveTab] = useState<TabId>(
    () => availableTabs[0] ?? "INSTRUCTIONS",
  );
  const [direction, setDirection] = useState(0);
  const [autoNavigatedFromInstructions, setAutoNavigatedFromInstructions] =
    useState(false);
  const [openStepIndex, setOpenStepIndex] = useState<number>(0);

  const teamExternalId = useEditorStore((state) => state.teamExternalId);

  useEffect(() => {
    AnalyticsApi.logInspirationGuideOpened(guide.id, teamExternalId);
  }, [guide.id, teamExternalId]);

  useEffect(() => {
    return subscribeInspirationAction((id) => {
      setOpenStepIndex((current) => {
        const openStep = guide.steps[current];
        if (!openStep || openStep.actionIdentifier !== id) return current;
        const next = current + 1;
        return next < guide.steps.length ? next : -1;
      });
    });
  }, [guide.steps]);

  const goToTab = (tab: TabId) => {
    if (tab === activeTab) return;
    const fromIdx = availableTabs.indexOf(activeTab);
    const toIdx = availableTabs.indexOf(tab);
    setDirection(toIdx > fromIdx ? 1 : -1);
    setActiveTab(tab);
  };

  const previousMainViewModeRef = useRef(mainView.mode);
  useEffect(() => {
    const previousMode = previousMainViewModeRef.current;
    previousMainViewModeRef.current = mainView.mode;
    if (previousMode === mainView.mode) return;
    if (activeTab === "INSTRUCTIONS") return;
    if (
      mainView.mode === "ANIMATION" &&
      availableTabs.includes("ANIMATION_PROMPTS")
    ) {
      goToTab("ANIMATION_PROMPTS");
    } else if (
      mainView.mode === "EDIT_IMAGE" &&
      availableTabs.includes("IMAGE_PROMPTS")
    ) {
      goToTab("IMAGE_PROMPTS");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainView.mode, activeTab, availableTabs]);

  const handleTabClick = (tab: TabId) => {
    goToTab(tab);
    setAutoNavigatedFromInstructions(false);
  };

  const handleSelectPromptFromStep = (step: InspirationGuideStep) => {
    const targetTab = getStepPromptTab(step);
    if (!targetTab) return;
    goToTab(targetTab);
    setAutoNavigatedFromInstructions(true);
  };

  const handlePromptApplied = () => {
    if (autoNavigatedFromInstructions) {
      goToTab("INSTRUCTIONS");
      setAutoNavigatedFromInstructions(false);
    }
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="px-6 pt-6">
        <div className="mb-6 flex items-center justify-between gap-3">
          <button
            className="-ml-1 flex cursor-pointer flex-row items-center gap-2 font-semibold text-white"
            onClick={onBack}
            type="button"
          >
            <IconChevronLeft className="text-white" width={20} height={20} />
            {guide.title}
          </button>

          <img
            src={`/inspiration/${guide.id}.jpg`}
            alt={guide.title}
            className="size-16 rounded-sm object-cover"
          />
        </div>
        <p className="mb-8 text-sm whitespace-pre-line text-white">
          {renderBold(guide.description)}
        </p>
        <TabNav
          tabs={availableTabs}
          activeTab={activeTab}
          onTabClick={handleTabClick}
        />
      </div>

      <div className="relative flex-1 overflow-hidden">
        <AnimatePresence custom={direction} initial={false}>
          <motion.div
            key={activeTab}
            custom={direction}
            variants={tabContentVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ type: "tween", duration: 0.3, ease: "easeOut" }}
            className="absolute inset-0 overflow-y-auto px-6 pt-6 pb-12"
          >
            {activeTab === "INSTRUCTIONS" && (
              <InstructionsTab
                guide={guide}
                openStepIndex={openStepIndex}
                onOpenStepIndexChange={setOpenStepIndex}
                onSelectPromptForStep={handleSelectPromptFromStep}
              />
            )}
            {activeTab === "ANIMATION_PROMPTS" && guide.animationPrompts && (
              <AnimationPromptsTab
                guideId={guide.id}
                prompts={guide.animationPrompts}
                mainView={mainView}
                autoNavigatedFromInstructions={autoNavigatedFromInstructions}
                onPromptApplied={handlePromptApplied}
              />
            )}
            {activeTab === "IMAGE_PROMPTS" && guide.imagePrompts && (
              <ImagePromptsTab
                guideId={guide.id}
                prompts={guide.imagePrompts}
                mainView={mainView}
                autoNavigatedFromInstructions={autoNavigatedFromInstructions}
                onPromptApplied={handlePromptApplied}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

function TabNav({
  tabs,
  activeTab,
  onTabClick,
}: {
  tabs: TabId[];
  activeTab: TabId;
  onTabClick: (tab: TabId) => void;
}) {
  return (
    <div className="relative flex flex-row gap-6 border-b border-white/15">
      {tabs.map((tab) => {
        const isActive = tab === activeTab;
        return (
          <button
            key={tab}
            type="button"
            onClick={() => onTabClick(tab)}
            className={cn(
              "relative -mb-px cursor-pointer pb-3 text-sm font-medium transition-colors",
              isActive ? "text-white" : "text-white/60 hover:text-white/80",
            )}
          >
            {TAB_LABELS[tab]}
            {isActive && (
              <motion.span
                layoutId="tab-nav-underline"
                className="absolute right-0 -bottom-px left-0 h-[1.5px] rounded-full bg-white"
                transition={{ type: "tween", duration: 0.3, ease: "easeOut" }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

function InstructionsTab({
  guide,
  openStepIndex,
  onOpenStepIndexChange,
  onSelectPromptForStep,
}: {
  guide: InspirationGuideType;
  openStepIndex: number;
  onOpenStepIndexChange: (index: number) => void;
  onSelectPromptForStep: (step: InspirationGuideStep) => void;
}) {
  const handleToggle = (idx: number) => {
    onOpenStepIndexChange(openStepIndex === idx ? -1 : idx);
  };

  return (
    <ol className="flex flex-col gap-5">
      {guide.steps.map((step, index) => (
        <StepItem
          key={index}
          index={index + 1}
          step={step}
          isOpen={index === openStepIndex}
          onToggle={() => handleToggle(index)}
          onSelectPrompt={() => onSelectPromptForStep(step)}
        />
      ))}
    </ol>
  );
}

function StepItem({
  index,
  step,
  isOpen,
  onToggle,
  onSelectPrompt,
}: {
  index: number;
  step: InspirationGuideStep;
  isOpen: boolean;
  onToggle: () => void;
  onSelectPrompt: () => void;
}) {
  const promptTab = getStepPromptTab(step);
  return (
    <li
      className={cn(
        "relative overflow-hidden rounded-xl border border-gray-300 bg-white transition-opacity duration-300",
        !isOpen && "border-white/15 bg-white/10",
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="flex w-full cursor-pointer items-center gap-3 px-5 py-4 text-left"
      >
        <StepBadge index={index} isOpen={isOpen} />
        <div
          className={cn(
            "min-w-0 flex-1 font-semibold",
            isOpen ? "text-text-primary" : "font-medium text-white/50",
          )}
        >
          {step.title}
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ type: "tween", duration: 0.2, ease: "easeOut" }}
          className="shrink-0"
        >
          <IconChevronDown
            size={18}
            className={cn(
              "transition-colors duration-300",
              isOpen ? "text-text-primary" : "text-text-secondary",
            )}
          />
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="content"
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ type: "tween", duration: 0.2, ease: "easeOut" }}
            style={{ overflow: "hidden" }}
          >
            <div className="px-5 pb-5 pl-14">
              <p className="text-sm text-text-primary">
                {renderBold(step.description)}
              </p>
              {promptTab && (
                <Button
                  size="sm"
                  variant={
                    promptTab === "IMAGE_PROMPTS" ? "tertiary" : "default"
                  }
                  className="mt-6"
                  onClick={onSelectPrompt}
                >
                  Select {promptTab === "IMAGE_PROMPTS" ? "image" : "animation"}{" "}
                  prompt
                </Button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
}

function StepBadge({ index, isOpen }: { index: number; isOpen: boolean }) {
  return (
    <div
      className={cn(
        "flex size-6 shrink-0 items-center justify-center rounded-full border text-xs font-semibold",
        isOpen
          ? "border-border text-text-primary/80"
          : "border-white/20 text-white/70",
      )}
    >
      {index}
    </div>
  );
}

function AnimationPromptsTab({
  guideId,
  prompts,
  mainView,
  autoNavigatedFromInstructions,
  onPromptApplied,
}: {
  guideId: string;
  prompts: InspirationAnimationPrompt[];
  mainView: MainViewState;
  autoNavigatedFromInstructions: boolean;
  onPromptApplied: () => void;
}) {
  const updateCurrentCreativeProjectState = useEditorStore(
    (state) => state.service.updateCurrentCreativeProjectState,
  );
  const teamExternalId = useEditorStore((state) => state.teamExternalId);

  const handleSelect = (prompt: InspirationAnimationPrompt) => {
    if (mainView.mode !== "ANIMATION") {
      Toast.error('You must go to "Custom animation" to use the prompt');
      return;
    }
    updateCurrentCreativeProjectState((draft) => {
      applyAnimatedImageCustomInput(draft, prompt.customInput);
    });
    AnalyticsApi.logAnimationPromptUsed(
      {
        guideId,
        promptId: prompt.id,
        autoNavigatedFromInstructions,
      },
      teamExternalId,
    );
    Toast.infoWhite("Prompt added to the form");
    onPromptApplied();
  };

  return (
    <div className="flex w-full flex-col gap-5">
      {prompts.map((prompt) => (
        <InspirationPromptListItem
          key={prompt.id}
          title={prompt.name}
          prompt={prompt.customInput.prompt}
          thumbnailSrc={`/inspiration/animation-prompts/${prompt.id}.jpg`}
          previewVideoSrc={
            prompt.hasPreviewVideo
              ? `/inspiration/animation-prompts/${prompt.id}.mp4`
              : undefined
          }
          ctaVariant="default"
          onUse={() => handleSelect(prompt)}
          onCopy={() =>
            copyToClipboard(
              prompt.customInput.prompt,
              "Prompt copied to clipboard",
            )
          }
        />
      ))}
    </div>
  );
}

function ImagePromptsTab({
  guideId,
  prompts,
  mainView,
  autoNavigatedFromInstructions,
  onPromptApplied,
}: {
  guideId: string;
  prompts: InspirationImagePrompt[];
  mainView: MainViewState;
  autoNavigatedFromInstructions: boolean;
  onPromptApplied: () => void;
}) {
  const updateCurrentCreativeProjectState = useEditorStore(
    (state) => state.service.updateCurrentCreativeProjectState,
  );
  const teamExternalId = useEditorStore((state) => state.teamExternalId);

  const handleSelect = (prompt: InspirationImagePrompt) => {
    if (mainView.mode !== "EDIT_IMAGE") {
      Toast.error('You must go to "Edit image" to use the prompt');
      return;
    }
    const sourceImageKey = mainView.sourceImageKey;
    updateCurrentCreativeProjectState((draft) => {
      applySourceImageCustomInput(draft, sourceImageKey, prompt.customInput);
    });
    AnalyticsApi.logImagePromptUsed(
      {
        guideId,
        promptId: prompt.id,
        autoNavigatedFromInstructions,
      },
      teamExternalId,
    );
    Toast.infoWhite("Prompt added to the form");
    onPromptApplied();
  };

  return (
    <div className="flex w-full flex-col gap-5">
      {prompts.map((prompt) => (
        <InspirationPromptListItem
          key={prompt.id}
          title={prompt.name}
          prompt={prompt.customInput.prompt}
          thumbnailSrc={`/inspiration/image-prompts/${prompt.id}.jpg`}
          previewVideoSrc={
            prompt.hasPreviewVideo
              ? `/inspiration/image-prompts/${prompt.id}.mp4`
              : undefined
          }
          ctaVariant="tertiary"
          onUse={() => handleSelect(prompt)}
          onCopy={() =>
            copyToClipboard(
              prompt.customInput.prompt,
              "Prompt copied to clipboard",
            )
          }
        />
      ))}
    </div>
  );
}

function renderBold(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return <span key={i}>{part}</span>;
  });
}

export default InspirationGuide;
